import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StaffPageShell from '../components/StaffPageShell';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import BatchChangeIndicators from '../components/Batch/BatchChangeIndicators';
import MessageDisplay from '../components/MessageDisplay';
import EnrollmentStudentCard from '../components/Batch/EnrollmentStudentCard';
import BatchTrainersPanel from '../components/Batch/BatchTrainersPanel';
import { formatDate } from '../utils/formatUtils';
import useBatchDetails from '../hooks/useBatchDetails';

const BatchDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [expandedStudentId, setExpandedStudentId] = useState(null);
  const { batch, loading, error, setBatch } = useBatchDetails(id);

  if (loading) {
    return (
      <StaffPageShell title="Batch Details" backTo="/admin/batches" backLabel="Batches">
        <LoadingSpinner message="Loading batch details..." />
      </StaffPageShell>
    );
  }

  if (error || !batch) {
    return (
      <StaffPageShell title="Batch Details" backTo="/admin/batches" backLabel="Batches">
        <MessageDisplay message={error || 'Batch not found'} />
      </StaffPageShell>
    );
  }

  return (
    <StaffPageShell title="Batch Details" backTo="/admin/batches" backLabel="Batches">
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold theme-text-gradient mb-2">{batch.batchName}</h1>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={batch.status} variant="pill" />
                <BatchChangeIndicators batch={batch} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate(`/admin/module-completion?batchId=${batch.id}`)}
                className="px-4 py-2 rounded-lg font-medium theme-button-secondary"
              >
                Module completion
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/batches`)}
                className="px-4 py-2 rounded-lg font-medium theme-button-primary"
              >
                Edit Batch
              </button>
            </div>
          </div>
        </div>

        <div className="batch-detail-grid grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="theme-card p-6 rounded-lg min-w-0">
            <h2 className="text-xl font-semibold mb-4 theme-text block w-full">Batch Information</h2>
            <div className="space-y-3 w-full">
              <div className="flex justify-between gap-4 items-start">
                <span className="font-medium theme-text-secondary shrink-0">Batch Name:</span>
                <span className="theme-text text-right break-words">{batch.batchName}</span>
              </div>
              <div className="flex justify-between gap-4 items-start">
                <span className="font-medium theme-text-secondary shrink-0">Course:</span>
                <span className="theme-text text-right break-words">{batch.course?.courseName || 'N/A'}</span>
              </div>
              {batch.course?.category && (
                <div className="flex justify-between gap-4 items-start">
                  <span className="font-medium theme-text-secondary shrink-0">Category:</span>
                  <span className="theme-text text-right break-words">{batch.course.category.categoryName}</span>
                </div>
              )}
              <div className="flex justify-between gap-4 items-start">
                <span className="font-medium theme-text-secondary shrink-0">Mode:</span>
                <span className="theme-text">{batch.mode}</span>
              </div>
              {batch.mode === 'Offline' && batch.location && (
                <div className="flex justify-between gap-4 items-start">
                  <span className="font-medium theme-text-secondary shrink-0">Location:</span>
                  <span className="theme-text">{batch.location}</span>
                </div>
              )}
              <div className="flex justify-between gap-4 items-start">
                <span className="font-medium theme-text-secondary shrink-0">Start Date:</span>
                <span className="theme-text">{formatDate(batch.startDate)}</span>
              </div>
              {batch.expectedCompletionDate && (
                <div className="flex justify-between gap-4 items-start">
                  <span className="font-medium theme-text-secondary shrink-0">Expected Completion:</span>
                  <span className="theme-text">{formatDate(batch.expectedCompletionDate)}</span>
                </div>
              )}
              <div className="flex justify-between gap-4 items-center">
                <span className="font-medium theme-text-secondary shrink-0">Status:</span>
                <StatusBadge status={batch.status} />
              </div>
              {batch.holdReason && (
                <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Hold Reason:</strong> {batch.holdReason}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="min-w-0">
            <BatchTrainersPanel
              batchId={batch.id}
              initialTrainers={batch.trainers || []}
              onChanged={(trainers) =>
                setBatch((prev) => (prev ? { ...prev, trainers } : prev))
              }
            />
          </div>
        </div>

        <div className="theme-card p-6 rounded-lg mb-6 min-w-0">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
            <h2 className="text-xl font-semibold theme-text">Enrolled Students</h2>
            <p className="text-sm theme-text-secondary">
              Total Students{' '}
              <span className="text-2xl font-bold theme-text align-middle">
                {batch.enrollments?.length || 0}
              </span>
            </p>
          </div>
          {batch.enrollments && batch.enrollments.length > 0 ? (
            <div className="max-h-[28rem] overflow-y-auto space-y-2">
              {batch.enrollments.map((enrollment) => (
                <EnrollmentStudentCard
                  key={enrollment.id}
                  enrollment={enrollment}
                  isExpanded={expandedStudentId === enrollment.id}
                  onToggle={() =>
                    setExpandedStudentId(
                      expandedStudentId === enrollment.id ? null : enrollment.id
                    )
                  }
                />
              ))}
            </div>
          ) : (
            <p className="text-sm theme-text-secondary">No students enrolled in this batch yet.</p>
          )}
        </div>

        {/* Additional Information */}
        {batch.createdAt && (
          <div className="mt-6 theme-card p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 theme-text">System Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="font-medium theme-text-secondary">Created:</span>
                <span className="ml-2 theme-text">{formatDate(batch.createdAt)}</span>
              </div>
              {batch.updatedAt && (
                <div>
                  <span className="font-medium theme-text-secondary">Last Updated:</span>
                  <span className="ml-2 theme-text">{formatDate(batch.updatedAt)}</span>
                </div>
              )}
            </div>
          </div>
        )}
    </StaffPageShell>
  );
};

export default BatchDetailPage;
